import * as actions from './Actions';

const parseEvidence = (payload) => {
  const url = payload.url ?? payload.evidence_url;
  const fileName = payload.file_name
    ?? (url ? String(url).split('/').pop() : undefined)
    ?? payload.original_name
    ?? payload.client_name;
  const evidenceType = payload.evidenceType ?? payload.evidence_type;
  const employeeObj = payload.employee || null;
  const employeeId = payload.employeeId ?? payload.employee_id ?? payload.evaluation?.employee_id ?? employeeObj?.id;
  const employeeUsername = payload.employeeUsername ?? employeeObj?.username ?? payload.evaluation?.employee?.username;
  return {
    id: payload.id ?? payload.evidenceId ?? payload.evidence_id ?? payload._id,
    evaluationId: payload.evaluationId ?? payload.evaluation_id,
    evidenceType,
    evidence_type: evidenceType, // keep legacy alias for UI selectors
    url,
    evidence_url: url, // keep alias in sync
    file_name: fileName,
    original_name: payload.original_name,
    client_name: payload.client_name,
    status: payload.status,
    metadata: payload.metadata,
    createdAt: payload.createdAt,
    updatedAt: payload.updatedAt,
    // Employee context
    employeeId: employeeId != null ? Number(employeeId) : undefined,
    employeeUsername: employeeUsername || undefined,
    employee: employeeObj,
  };
};

export const practicalEvidencesReducer = (state = [], action) => {
  switch (action.type) {
    case actions.INITIALIZE_PRACTICAL_EVIDENCES: {
      const incomingList = (action.payload || []).map(parseEvidence);
      const items = Array.isArray(state) ? state : (state.items || []);
      const tombstones = Array.isArray(state) ? [] : (state.tombstones || []);
      const byId = new Map(items.map((e) => [String(e.id), e]));

      const merged = [];
      for (const inc of incomingList) {
        const key = String(inc.id);
        if (tombstones.includes(key)) {
          continue;
        }
        const existing = byId.get(key);
        if (!existing) {
          merged.push(inc);
          continue;
        }
        // Prefer the most recently updated record
        const incTime = inc.updatedAt ? Date.parse(inc.updatedAt) : 0;
        const exTime = existing.updatedAt ? Date.parse(existing.updatedAt) : 0;
        const newer = (incTime || 0) >= (exTime || 0) ? inc : existing;
        const older = newer === inc ? existing : inc;
        // Preserve display names if missing in newer
        const file_name = newer.file_name ?? older.file_name;
        const original_name = newer.original_name ?? older.original_name;
        const client_name = newer.client_name ?? older.client_name;
        const url = newer.url ?? newer.evidence_url ?? older.url ?? older.evidence_url;
        const evidence_url = newer.evidence_url ?? newer.url ?? older.evidence_url ?? older.url;
        const evidenceType = newer.evidenceType ?? newer.evidence_type ?? older.evidenceType ?? older.evidence_type;
        // Preserve employee context
        const employeeId = newer.employeeId ?? older.employeeId;
        const employeeUsername = newer.employeeUsername ?? older.employeeUsername;
        const employee = newer.employee ?? older.employee ?? null;
        const mergedRow = {
          ...existing,
          ...newer,
          url,
          evidence_url,
          evidenceType,
          evidence_type: evidenceType,
          file_name,
          original_name,
          client_name,
          employeeId,
          employeeUsername,
          employee,
        };
        merged.push(mergedRow);
        byId.delete(key);
      }
      // Keep any remaining local-only items (optimistic or not returned by API page)
      for (const remain of byId.values()) merged.push(remain);
      // Always return object shape so tombstones persist across subsequent actions
      return { ...(Array.isArray(state) ? {} : state), items: merged, tombstones };
    }

    case actions.ADD_PRACTICAL_EVIDENCE: {
      const incomingRaw = action.payload || {};
      const incoming = parseEvidence(incomingRaw);
      const items = Array.isArray(state) ? state : (state.items || []);
      let replaced = false;
      const next = items.map((e) => {
        if (e.id === incoming.id) {
          replaced = true;
          const mergedUrl = incoming.url ?? e.evidence_url ?? e.url;
          const mergedFileName = incoming.file_name ?? (mergedUrl ? String(mergedUrl).split('/').pop() : e.file_name);
          const mergedType = incoming.evidenceType ?? incoming.evidence_type ?? e.evidenceType ?? e.evidence_type;
          return {
            ...e,
            ...incoming,
            // keep both canonical and original keys in sync for UI selectors
            url: mergedUrl,
            evidence_url: mergedUrl,
            file_name: mergedFileName,
            original_name: incoming.original_name ?? e.original_name,
            client_name: incoming.client_name ?? e.client_name,
            evidenceType: mergedType,
            evidence_type: mergedType,
            // employee context
            employeeId: incoming.employeeId ?? e.employeeId,
            employeeUsername: incoming.employeeUsername ?? e.employeeUsername,
            employee: incoming.employee ?? e.employee ?? null,
          };
        }
        return e;
      });
      const insertUrl = incoming.url ?? incomingRaw.evidence_url;
      const insertFileName = incoming.file_name ?? (insertUrl ? String(insertUrl).split('/').pop() : undefined);
      const insertType = incoming.evidenceType ?? incoming.evidence_type;
      const normalizedIncoming = {
        ...incoming,
        url: insertUrl ?? incoming.url,
        evidence_url: insertUrl ?? incoming.evidence_url,
        file_name: insertFileName ?? incoming.file_name,
        original_name: incoming.original_name ?? incomingRaw.original_name,
        client_name: incoming.client_name ?? incomingRaw.client_name,
        evidenceType: insertType,
        evidence_type: insertType,
        // passthrough employee context
        employeeId: incoming.employeeId,
        employeeUsername: incoming.employeeUsername,
        employee: incoming.employee ?? incomingRaw.employee ?? null,
      };
      const finalItems = replaced ? next : [...next, normalizedIncoming];
      const tombstones = Array.isArray(state) ? [] : (state.tombstones || []);
      // Always return object shape
      return { ...(Array.isArray(state) ? {} : state), items: finalItems, tombstones };
    }

    case actions.UPDATE_PRACTICAL_EVIDENCE: {
      const payload = action.payload || {};
      const id = payload.id ?? payload.evidenceId ?? payload.evidence_id ?? payload._id;
      const items = Array.isArray(state) ? state : (state.items || []);
      let found = false;
      const parsed = parseEvidence({ ...payload, id });
      const nextItems = items.map((e) => {
        if (e.id === id) {
          found = true;
          const incomingUrl = payload.evidence_url ?? payload.url;
          const mergedUrl = incomingUrl ?? e.evidence_url ?? e.url;
          const computedFileName = payload.file_name
            ?? (incomingUrl ? String(incomingUrl).split('/').pop() : e.file_name);
          return {
            ...e,
            // Only apply defined fields from parsed to avoid wiping existing values
            ...(parsed.evaluationId !== undefined ? { evaluationId: parsed.evaluationId } : {}),
            ...(parsed.evidenceType !== undefined ? { evidenceType: parsed.evidenceType } : {}),
            ...(parsed.status !== undefined ? { status: parsed.status } : {}),
            ...(parsed.metadata !== undefined ? { metadata: parsed.metadata } : {}),
            ...(parsed.createdAt !== undefined ? { createdAt: parsed.createdAt } : {}),
            ...(parsed.employeeId !== undefined ? { employeeId: parsed.employeeId } : {}),
            ...(parsed.employeeUsername !== undefined ? { employeeUsername: parsed.employeeUsername } : {}),
            ...(payload.employee !== undefined ? { employee: payload.employee } : {}),
            ...(payload.original_name !== undefined ? { original_name: payload.original_name } : {}),
            ...(payload.client_name !== undefined ? { client_name: payload.client_name } : {}),
            // keep both canonical and original keys in sync for UI selectors
            url: parsed.url ?? mergedUrl,
            evidence_url: mergedUrl,
            file_name: computedFileName,
            updatedAt: payload.updatedAt || parsed.updatedAt || new Date().toISOString(),
          };
        }
        return e;
      });
      const mergedParsedForInsert = {
        ...parsed,
        evidence_url: parsed.url ?? payload.evidence_url,
        url: parsed.url ?? payload.url ?? payload.evidence_url,
        file_name: payload.file_name ?? ((payload.evidence_url || payload.url) ? String(payload.evidence_url || payload.url).split('/').pop() : undefined),
        original_name: payload.original_name,
        client_name: payload.client_name,
        employee: payload.employee ?? parsed.employee ?? null,
        updatedAt: payload.updatedAt || new Date().toISOString(),
      };
      const finalItems = found ? nextItems : [...nextItems, mergedParsedForInsert];
      const tombstones = Array.isArray(state) ? [] : (state.tombstones || []);
      // Always return object shape
      return { ...(Array.isArray(state) ? {} : state), items: finalItems, tombstones };
    }

    case actions.APPROVE_PRACTICAL_EVIDENCE: {
      const payload = action.payload || {};
      const id = payload.id ?? payload.evidenceId ?? payload.evidence_id ?? payload.data?.evidence_id;
      const mergedParsed = parseEvidence({ ...payload, id, status: 'approved' });
      const items = Array.isArray(state) ? state : (state.items || []);
      let found = false;
      const nextItems = items.map((e) => {
        if (e.id === id) {
          found = true;
          return { ...e, ...mergedParsed, status: 'approved' };
        }
        return e;
      });
      const finalItems = found ? nextItems : [...nextItems, mergedParsed];
      const tombstones = Array.isArray(state) ? [] : (state.tombstones || []);
      return { ...(Array.isArray(state) ? {} : state), items: finalItems, tombstones };
    }

    case actions.REJECT_PRACTICAL_EVIDENCE: {
      const payload = action.payload || {};
      const id = payload.id ?? payload.evidenceId ?? payload.evidence_id ?? payload.data?.evidence_id;
      const mergedParsed = parseEvidence({ ...payload, id, status: 'rejected' });
      const items = Array.isArray(state) ? state : (state.items || []);
      let found = false;
      const nextItems = items.map((e) => {
        if (e.id === id) {
          found = true;
          return { ...e, ...mergedParsed, status: 'rejected' };
        }
        return e;
      });
      const finalItems = found ? nextItems : [...nextItems, mergedParsed];
      const tombstones = Array.isArray(state) ? [] : (state.tombstones || []);
      return { ...(Array.isArray(state) ? {} : state), items: finalItems, tombstones };
    }

    case actions.DELETE_PRACTICAL_EVIDENCE: {
      const id = action.evidenceId ?? action.id;
      const items = Array.isArray(state) ? state : (state.items || []);
      const key = String(id);
      const nextItems = items.filter((e) => String(e.id) !== key);
      const tombstones = Array.isArray(state) ? [] : (state.tombstones || []);
      const nextTombstones = tombstones.includes(key) ? tombstones : [...tombstones, key];
      // Always return object shape
      return { ...(Array.isArray(state) ? {} : state), items: nextItems, tombstones: nextTombstones };
    }

    case actions.CLEAR_PRACTICAL_EVIDENCES_LIST:
      // Keep object shape consistent
      return { items: [], tombstones: [] };

    default:
      return state;
  }
};
